import React, { useEffect, useState, createContext, useContext } from 'react';
import { courseData } from '../utils/courseData';
export interface Course {
  id: string;
  title: string;
  description: string;
  thumbnail: string;
  modules: Module[];
  isRequired: boolean;
  isSuggested: boolean;
}
export interface Module {
  id: string;
  title: string;
  content: {
    video?: string;
    text?: string;
    resources?: {
      name: string;
      url: string;
    }[];
  };
}
interface UserProgress {
  courseId: string;
  preAssessmentCompleted: boolean;
  preAssessmentScore?: number;
  completedModules: string[];
  postAssessmentCompleted: boolean;
  postAssessmentScore?: number;
  certificateGenerated: boolean;
}
interface CourseContextType {
  courses: Course[];
  userProgress: UserProgress[];
  enrollInCourse: (courseId: string) => void;
  completePreAssessment: (courseId: string, score: number) => void;
  completeModule: (courseId: string, moduleId: string) => void;
  completePostAssessment: (courseId: string, score: number) => void;
  generateCertificate: (courseId: string) => void;
  getCourseProgress: (courseId: string) => UserProgress | undefined;
  isEnrolled: (courseId: string) => boolean;
}
const CourseContext = createContext<CourseContextType | undefined>(undefined);
export const CourseProvider: React.FC<{
  children: React.ReactNode;
}> = ({
  children
}) => {
  const [courses] = useState<Course[]>(courseData);
  const [userProgress, setUserProgress] = useState<UserProgress[]>([]);
  useEffect(() => {
    // Load progress from localStorage
    const savedProgress = localStorage.getItem('courseProgress');
    if (savedProgress) {
      setUserProgress(JSON.parse(savedProgress));
    }
  }, []);
  // Save progress to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('courseProgress', JSON.stringify(userProgress));
  }, [userProgress]);
  const enrollInCourse = (courseId: string) => {
    if (!isEnrolled(courseId)) {
      setUserProgress([...userProgress, {
        courseId,
        preAssessmentCompleted: false,
        completedModules: [],
        postAssessmentCompleted: false,
        certificateGenerated: false
      }]);
    }
  };
  const completePreAssessment = (courseId: string, score: number) => {
    setUserProgress(userProgress.map(progress => progress.courseId === courseId ? {
      ...progress,
      preAssessmentCompleted: true,
      preAssessmentScore: score
    } : progress));
  };
  const completeModule = (courseId: string, moduleId: string) => {
    setUserProgress(userProgress.map(progress => {
      if (progress.courseId === courseId && !progress.completedModules.includes(moduleId)) {
        return {
          ...progress,
          completedModules: [...progress.completedModules, moduleId]
        };
      }
      return progress;
    }));
  };
  const completePostAssessment = (courseId: string, score: number) => {
    setUserProgress(userProgress.map(progress => progress.courseId === courseId ? {
      ...progress,
      postAssessmentCompleted: true,
      postAssessmentScore: score
    } : progress));
  };
  const generateCertificate = (courseId: string) => {
    setUserProgress(userProgress.map(progress => progress.courseId === courseId ? {
      ...progress,
      certificateGenerated: true
    } : progress));
  };
  const getCourseProgress = (courseId: string) => {
    return userProgress.find(progress => progress.courseId === courseId);
  };
  const isEnrolled = (courseId: string) => {
    return userProgress.some(progress => progress.courseId === courseId);
  };
  return <CourseContext.Provider value={{
    courses,
    userProgress,
    enrollInCourse,
    completePreAssessment,
    completeModule,
    completePostAssessment,
    generateCertificate,
    getCourseProgress,
    isEnrolled
  }} data-id="element-313">
      {children}
    </CourseContext.Provider>;
};
export const useCourses = () => {
  const context = useContext(CourseContext);
  if (context === undefined) {
    throw new Error('useCourses must be used within a CourseProvider');
  }
  return context;
};